import React from 'react';
import { BarChart, ChevronDown, Info, ArrowRight } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации индекса массы тела
 */
const BmiChart = ({ bmi, category, weight, height, goalWeight }) => {
  if (!bmi) return null;

  // Данные о категориях ИМТ
  const bmiCategories = [
    { min: 0, max: 16, label: 'Выраженный дефицит', color: 'bg-blue-500', textColor: 'text-blue-700 dark:text-blue-300' },
    { min: 16, max: 18.5, label: 'Недостаточный вес', color: 'bg-blue-400', textColor: 'text-blue-600 dark:text-blue-400' },
    { min: 18.5, max: 25, label: 'Нормальный вес', color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-300' },
    { min: 25, max: 30, label: 'Избыточный вес', color: 'bg-yellow-500', textColor: 'text-yellow-700 dark:text-yellow-300' },
    { min: 30, max: 35, label: 'Ожирение I', color: 'bg-orange-500', textColor: 'text-orange-700 dark:text-orange-300' },
    { min: 35, max: 40, label: 'Ожирение II', color: 'bg-red-500', textColor: 'text-red-700 dark:text-red-300' },
    { min: 40, max: 100, label: 'Ожирение III', color: 'bg-red-600', textColor: 'text-red-700 dark:text-red-300' }
  ];

  // Находим текущую категорию
  const currentCategory = bmiCategories.find(cat => bmi >= cat.min && bmi < cat.max);
  
  // Вычисляем позицию маркера на шкале (в процентах)
  const markerPosition = Math.min(100, Math.max(0, (bmi - 10) / 30 * 100));
  
  // Определяем позицию для идеального веса
  const idealBmi = goalWeight / Math.pow(height / 100, 2);
  const idealMarkerPosition = Math.min(100, Math.max(0, (idealBmi - 10) / 30 * 100));
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Визуализация индекса массы тела
        </h3>
      </div>

      {/* Инфографика со шкалой ИМТ */}
      <div className="mb-6">
        <div className="relative h-24">
          {/* Шкала ИМТ */}
          <div className="absolute bottom-10 left-0 right-0 h-7 rounded-lg overflow-hidden flex">
            {bmiCategories.map((category, index) => {
              // Вычисляем ширину каждой категории относительно всей шкалы
              const width = ((category.max - category.min) / 40) * 100;
              return (
                <div
                  key={index}
                  className={`${category.color} h-full`}
                  style={{ width: `${width}%` }}
                  title={`${category.label}: ${category.min} - ${category.max}`}
                />
              );
            })}
          </div>

          {/* Метки значений ИМТ */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400 px-1">
            <span>10</span>
            <span>15</span>
            <span>20</span>
            <span>25</span>
            <span>30</span>
            <span>35</span>
            <span>40+</span>
          </div>

          {/* Метки категорий */}
          <div className="absolute bottom-12 left-0 right-0 flex text-xxs">
            {bmiCategories.map((category, index) => {
              const position = ((category.min - 10) / 30) * 100;
              const width = ((category.max - category.min) / 30) * 100;
              return (
                <div
                  key={index}
                  className={`${category.textColor} font-medium text-[10px] overflow-hidden whitespace-nowrap text-center`}
                  style={{ 
                    marginLeft: index === 0 ? 0 : `${position}%`, 
                    width: `${width}%` 
                  }}
                >
                  {category.label}
                </div>
              );
            })}
          </div>

          {/* Маркер текущего ИМТ */}
          <div 
            className="absolute bottom-8"
            style={{ left: `calc(${markerPosition}% - 8px)` }}
          >
            <div className="flex flex-col items-center">
              <div className="bg-white dark:bg-gray-900 shadow-md rounded-full h-7 w-7 flex items-center justify-center border-2 border-emerald-500 z-10">
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">{formatNumber(bmi, 1)}</span>
              </div>
              <ChevronDown className="w-5 h-5 text-emerald-500 -mt-1" />
            </div>
          </div>

          {/* Маркер идеального ИМТ */}
          {goalWeight && (
            <div 
              className="absolute bottom-8"
              style={{ left: `calc(${idealMarkerPosition}% - 8px)` }}
            >
              <div className="flex flex-col items-center">
                <div className="bg-white dark:bg-gray-900 shadow-md rounded-full h-7 w-7 flex items-center justify-center border-2 border-purple-500 z-10">
                  <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400">{formatNumber(idealBmi, 1)}</span>
                </div>
                <ChevronDown className="w-5 h-5 text-purple-500 -mt-1" />
              </div>
            </div>
          )}
        </div>

        {/* Легенда */}
        <div className="flex justify-center gap-6 mt-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-xs text-gray-700 dark:text-gray-300">Ваш текущий ИМТ</span>
          </div>
          {goalWeight && (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-xs text-gray-700 dark:text-gray-300">Целевой ИМТ</span>
            </div>
          )}
        </div>
      </div>

      {/* Подробная информация */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <Info className="w-4 h-4 mr-1.5 text-blue-500" />
            Ваша категория: {currentCategory?.label}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Согласно классификации ВОЗ, ваш ИМТ {formatNumber(bmi, 1)} кг/м² относится к категории 
            "{category}". {getCategoryDescription(bmi)}
          </p>
        </div>

        {weight && goalWeight && (
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <ArrowRight className="w-4 h-4 mr-1.5 text-purple-500" />
              Путь к целевому весу
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {getWeightPathDescription(weight, goalWeight)}
            </p>
          </div>
        )}
      </div>

      {/* Статистика по России */}
      <div className="mt-5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Статистика по России</h4>
        <p className="text-xs text-blue-600 dark:text-blue-400">
          По данным Минздрава РФ и Росстата (на 2023 год), около 57% взрослых россиян имеют избыточную 
          массу тела, а 23% страдают от ожирения. Эти показатели растут в последние годы, что связано с 
          малоподвижным образом жизни и нерациональным питанием.
        </p>
      </div>
    </div>
  );
};

// Вспомогательная функция для получения описания категории
const getCategoryDescription = (bmi) => {
  if (bmi < 16) {
    return "У вас выраженный дефицит массы тела, что может указывать на серьезные проблемы со здоровьем. Необходима консультация специалиста.";
  } else if (bmi < 18.5) {
    return "У вас недостаточная масса тела. Рекомендуется пересмотреть рацион питания и проконсультироваться с врачом.";
  } else if (bmi < 25) {
    return "Ваш вес находится в пределах нормы. Это оптимальный показатель для здоровья.";
  } else if (bmi < 30) {
    return "У вас избыточная масса тела. Рекомендуется более внимательно следить за питанием и увеличить физическую активность.";
  } else if (bmi < 35) {
    return "У вас ожирение I степени. Это повышает риск развития сердечно-сосудистых заболеваний, диабета и других проблем со здоровьем.";
  } else if (bmi < 40) {
    return "У вас ожирение II степени (выраженное). Настоятельно рекомендуется обратиться к врачу для разработки плана по снижению веса.";
  } else {
    return "У вас ожирение III степени (морбидное). Это серьезное состояние, требующее медицинского вмешательства и наблюдения специалистов.";
  }
};

// Вспомогательная функция для описания пути к целевому весу
const getWeightPathDescription = (currentWeight, goalWeight) => {
  const weightDiff = currentWeight - goalWeight;
  
  if (Math.abs(weightDiff) < 0.5) {
    return `Ваш текущий вес (${formatNumber(currentWeight, 1)} кг) практически соответствует рекомендуемому (${formatNumber(goalWeight, 1)} кг). Для поддержания веса важен баланс питания и физической активности.`;
  } else if (weightDiff > 0) {
    return `Для достижения рекомендуемого веса (${formatNumber(goalWeight, 1)} кг) вам необходимо снизить вес на ${formatNumber(weightDiff, 1)} кг. При здоровом темпе снижения 0,5-1 кг в неделю это займет примерно ${Math.ceil(weightDiff / 0.75)} недель.`;
  } else {
    return `Для достижения рекомендуемого веса (${formatNumber(goalWeight, 1)} кг) вам необходимо набрать ${formatNumber(Math.abs(weightDiff), 1)} кг. Рекомендуется увеличить калорийность рациона и включить силовые тренировки для здорового набора массы.`;
  }
};

export default BmiChart;